"use client";

import { useEffect, type ReactNode } from "react";
import { useSearchParams } from "next/navigation";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@restomat/components/ui";

export interface FaqItem {
  id: string;
  question: string;
  answer: ReactNode;
}

export function FaqAccordion({ items }: { items: FaqItem[] }) {
  const searchParams = useSearchParams();
  const questionId = searchParams.get("otazka");

  // Scroll to the specific question when the page loads with a query parameter
  useEffect(() => {
    if (questionId) {
      // Small delay to ensure the accordion is rendered
      const timer = setTimeout(() => {
        const element = document.getElementById(`faq-${questionId}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [questionId]);

  // Determine which accordion items should be open by default
  const defaultValue = questionId
    ? items.find((item) => item.id === questionId)
      ? [questionId]
      : []
    : [];

  return (
    <Accordion
      type="multiple"
      defaultValue={defaultValue}
      className="w-full"
    >
      {items.map((item) => (
        <AccordionItem
          key={item.id}
          value={item.id}
          id={`faq-${item.id}`}
          className="border-b border-border"
        >
          <AccordionTrigger className="text-left text-base md:text-lg py-6 hover:no-underline">
            {item.question}
          </AccordionTrigger>
          <AccordionContent className="text-muted-foreground pb-6">
            {item.answer}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
